import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X } from "lucide-react";

const empty = {
  material: "Aço Carbono",
  thickness_mm: 3,
  price_per_m2: 0,
  density_kg_m3: 7850,
  notes: "",
};

const materials = ["Aço Carbono", "Aço Inox 304", "Aço Inox 316", "Alumínio", "Cobre", "Latão"];

export default function Sheets() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/sheets").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/sheets/${editId}`, form);
      else await api.post("/sheets", form);
      toast.success("Chapa salva");
      setOpen(false);
      setForm(empty);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir chapa?")) return;
    await api.delete(`/sheets/${id}`);
    toast.success("Excluída");
    load();
  };

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] mono uppercase tracking-[0.3em] text-orange-400/80">
            cadastros · matéria-prima
          </div>
          <h1 className="display text-3xl font-bold mt-1">Catálogo de Chapas</h1>
        </div>
        <button
          data-testid="sheet-new-button"
          onClick={() => {
            setEditId(null);
            setForm(empty);
            setOpen(true);
          }}
          className="pill-accent rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nova chapa
        </button>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sheets-grid">
        {list.map((s) => (
          <div key={s.id} className="card-tactical rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-orange-400/80">
                  {s.material}
                </div>
                <div className="display text-2xl font-bold mt-1">
                  {s.thickness_mm} <span className="text-sm mono text-slate-400">mm</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  className="p-1.5 rounded hover:bg-slate-700 text-slate-300"
                  onClick={() => {
                    setForm({ ...empty, ...s });
                    setEditId(s.id);
                    setOpen(true);
                  }}
                  data-testid={`sheet-edit-${s.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  className="p-1.5 rounded hover:bg-red-900/40 text-red-300"
                  onClick={() => remove(s.id)}
                  data-testid={`sheet-delete-${s.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between items-baseline">
              <span className="text-xs text-slate-400">R$ / m²</span>
              <span className="mono text-xl text-orange-300">{brl(s.price_per_m2)}</span>
            </div>
            <div className="mt-1 flex justify-between text-xs text-slate-500">
              <span>densidade</span>
              <span className="mono">{s.density_kg_m3} kg/m³</span>
            </div>
            {s.notes && <div className="mt-2 text-xs text-slate-400">{s.notes}</div>}
          </div>
        ))}
        {list.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-14 text-sm">
            Nenhuma chapa cadastrada
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
          <form onSubmit={save} className="card-tactical rounded-lg w-full max-w-md p-6 relative">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="display text-xl font-semibold mb-4">
              {editId ? "Editar chapa" : "Nova chapa"}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs uppercase text-slate-400">Material</label>
                <select
                  value={form.material}
                  onChange={(e) => setForm({ ...form, material: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                >
                  {materials.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-400">Espessura (mm)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  data-testid="sheet-thickness-input"
                  value={form.thickness_mm}
                  onChange={(e) =>
                    setForm({ ...form, thickness_mm: parseFloat(e.target.value) || 0 })
                  }
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-400">Preço R$/m²</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  data-testid="sheet-price-input"
                  value={form.price_per_m2}
                  onChange={(e) =>
                    setForm({ ...form, price_per_m2: parseFloat(e.target.value) || 0 })
                  }
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-400">Densidade kg/m³</label>
                <input
                  type="number"
                  step="1"
                  value={form.density_kg_m3}
                  onChange={(e) =>
                    setForm({ ...form, density_kg_m3: parseFloat(e.target.value) || 0 })
                  }
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm mono"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase text-slate-400">Notas</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded text-sm text-slate-300 hover:bg-slate-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="sheet-form-submit-button"
                className="pill-accent px-4 py-2 rounded text-sm font-semibold"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
